import { useState, useMemo, useEffect } from "react";
import ReactDOM from "react-dom";
import { T } from "../utils/theme";
import { api, ENDPOINTS, formatDate } from "../utils/api";
import { useFetch, useToast, useModal } from "../hooks";
import {
  Card, Spinner, EmptyState, SectionHeader, SearchInput,
  Tabs, ProgressBar, AlertBox, ToastStack,
} from "../components/UI";
import {
  Users, CheckCircle, Clock, Star, Check, Eye,
  Award, TrendingUp, UserCheck,
  FileText, ChevronRight, RefreshCw, Camera,
  Download, X, ZoomIn, XCircle, ThumbsUp, ThumbsDown,
} from "lucide-react";

// ─── Niveaux ──────────────────────────────────────────────────────────────────
const LEVEL_CFG = {
  "Expert":        { color: T.blue,   bg: `${T.blue}15`,   icon: "⚡" },
  "Intermediaire": { color: T.purple, bg: `${T.purple}15`, icon: "🔥" },
  "Intermédiaire": { color: T.purple, bg: `${T.purple}15`, icon: "🔥" },
  "Novice":        { color: T.muted,  bg: `${T.muted}15`,  icon: "🌱" },
};

const LEVEL_RANGES = {
  "Expert":        { min: 1000, max: 9999 },
  "Intermediaire": { min: 300,  max: 999  },
  "Intermédiaire": { min: 300,  max: 999  },
  "Novice":        { min: 0,    max: 299  },
};

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

// ─── Helpers fichiers ─────────────────────────────────────────────────────────
const parseFiles = (raw) => {
  if (!raw) return [];
  return raw.split("|").map(p => p.trim()).filter(Boolean);
};

const fileUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${API_BASE}/${path.replace(/^\//, "")}`;
};

const isImage = (path) => /\.(png|jpg|jpeg|gif|webp)$/i.test(path);
const isPdf   = (path) => /\.pdf$/i.test(path);

// ─── Visionneuse de document ──────────────────────────────────────────────────
const DocViewer = ({ files, label, onClose }) => {
  const [idx, setIdx] = useState(0);
  const file = files[idx];
  const url  = fileUrl(file);

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 2000,
        background: "rgba(0,0,0,.88)",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: "absolute", top: 0, left: 0, right: 0,
          padding: "14px 20px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "rgba(0,0,0,.55)", backdropFilter: "blur(6px)",
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>{label}</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {files.length > 1 && (
            <span style={{ color: "#aaa", fontSize: 12 }}>
              {idx + 1} / {files.length}
            </span>
          )}
          <a
            href={url} target="_blank" rel="noreferrer" download
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "6px 14px", borderRadius: 8,
              background: T.blue, color: "#fff",
              fontSize: 12, fontWeight: 600, textDecoration: "none",
            }}
          >
            <Download size={13} /> Télécharger
          </a>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,.12)", border: "none",
              color: "#fff", borderRadius: 8, padding: "6px 10px", cursor: "pointer",
            }}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <div onClick={e => e.stopPropagation()} style={{ maxWidth: "90vw", maxHeight: "80vh" }}>
        {isImage(file) ? (
          <img
            src={url} alt={label}
            style={{ maxWidth: "90vw", maxHeight: "80vh", objectFit: "contain", borderRadius: 8 }}
          />
        ) : isPdf(file) ? (
          <iframe
            src={url} title={label}
            style={{ width: "80vw", height: "80vh", border: "none", borderRadius: 8 }}
          />
        ) : (
          <div style={{ color: "#fff", padding: 40, textAlign: "center" }}>
            <FileText size={48} style={{ marginBottom: 12, opacity: .6 }} />
            <div style={{ marginBottom: 16 }}>Aperçu non disponible</div>
            <a href={url} target="_blank" rel="noreferrer" download
              style={{ color: T.blue, fontWeight: 700 }}>
              Ouvrir le fichier
            </a>
          </div>
        )}
      </div>

      {files.length > 1 && (
        <div style={{ display: "flex", gap: 10, marginTop: 16 }} onClick={e => e.stopPropagation()}>
          {files.map((f, i) => (
            <button key={i} onClick={() => setIdx(i)} style={{
              padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
              background: i === idx ? T.blue : "rgba(255,255,255,.15)",
              color: "#fff", fontSize: 12, fontWeight: 600,
            }}>
              {isImage(f) ? `Photo ${i + 1}` : `Fichier ${i + 1}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── DocRow ───────────────────────────────────────────────────────────────────
const DocRow = ({ icon: Icon, label, desc, rawPath, docStatus, onApprove, onReject, kycAlreadyVerified }) => {
  const [viewer, setViewer]           = useState(false);
  const [localStatus, setLocalStatus] = useState(docStatus ?? null);

  const files     = parseFiles(rawPath);
  const submitted = files.length > 0;
  const RED       = "#ef4444";

  const borderColor =
    localStatus === "approved" ? T.green :
    localStatus === "rejected" ? RED :
    T.border;

  const handleApprove = () => { setLocalStatus("approved"); onApprove?.(); };
  const handleReject  = () => { setLocalStatus("rejected");  onReject?.();  };

  return (
    <>
      <div style={{
        background: T.bg,
        border: `1px solid ${borderColor}`,
        borderRadius: 10,
        overflow: "hidden",
        transition: "border-color .2s",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px" }}>
          <div style={{
            width: 36, height: 36, borderRadius: 9, flexShrink: 0,
            background:
              localStatus === "approved" ? `${T.green}15` :
              localStatus === "rejected" ? `${RED}15`     : `${T.blue}12`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {localStatus === "approved" ? <CheckCircle size={16} color={T.green} /> :
             localStatus === "rejected"  ? <XCircle    size={16} color={RED}      /> :
             <Icon size={16} color={T.blue} />}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
            <div style={{ fontSize: 11, color: T.muted }}>{desc}</div>
            {submitted && (
              <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>
                {files.length} fichier{files.length > 1 ? "s" : ""}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 7, alignItems: "center", flexShrink: 0 }}>
            <span className="badge" style={{
              color:
                localStatus === "approved" ? T.green :
                localStatus === "rejected" ? RED :
                submitted ? T.yellow : T.muted,
              background:
                localStatus === "approved" ? `${T.green}12` :
                localStatus === "rejected" ? `${RED}12` :
                submitted ? `${T.yellow}12` : `${T.muted}12`,
            }}>
              {localStatus === "approved" ? "✓ Validé"
               : localStatus === "rejected" ? "✗ Rejeté"
               : submitted ? "Soumis" : "En attente"}
            </span>

            {submitted && (
              <>
                <button
                  onClick={() => setViewer(true)}
                  title="Visualiser le document"
                  style={{
                    display: "flex", alignItems: "center", gap: 4,
                    padding: "5px 10px", borderRadius: 7, border: "none",
                    background: `${T.blue}15`, color: T.blue,
                    fontSize: 11, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  <ZoomIn size={12} /> Voir
                </button>

                <a
                  href={fileUrl(files[0])} target="_blank" rel="noreferrer" download
                  title="Télécharger"
                  style={{
                    display: "flex", alignItems: "center", gap: 4,
                    padding: "5px 10px", borderRadius: 7,
                    background: `${T.cyan}15`, color: T.cyan,
                    fontSize: 11, fontWeight: 600, textDecoration: "none",
                  }}
                >
                  <Download size={12} /> DL
                </a>
              </>
            )}
          </div>
        </div>

        {submitted && !kycAlreadyVerified && (
          <div style={{ display: "flex", borderTop: `1px solid ${T.border}` }}>
            <button
              onClick={handleApprove}
              disabled={localStatus === "approved"}
              style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                padding: "8px 0", border: "none",
                cursor: localStatus === "approved" ? "default" : "pointer",
                background: localStatus === "approved" ? `${T.green}20` : "transparent",
                color: localStatus === "approved" ? T.green : T.muted,
                fontSize: 11, fontWeight: 700,
                borderRight: `1px solid ${T.border}`,
                transition: "all .15s",
              }}
            >
              <ThumbsUp size={12} />
              {localStatus === "approved" ? "Validé ✓" : "Valider"}
            </button>

            <button
              onClick={handleReject}
              disabled={localStatus === "rejected"}
              style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                padding: "8px 0", border: "none",
                cursor: localStatus === "rejected" ? "default" : "pointer",
                background: localStatus === "rejected" ? `${RED}20` : "transparent",
                color: localStatus === "rejected" ? RED : T.muted,
                fontSize: 11, fontWeight: 700,
                transition: "all .15s",
              }}
            >
              <ThumbsDown size={12} />
              {localStatus === "rejected" ? "Rejeté ✗" : "Rejeter"}
            </button>
          </div>
        )}
      </div>

      {viewer && (
        <DocViewer files={files} label={label} onClose={() => setViewer(false)} />
      )}
    </>
  );
};

// ─── Badges ───────────────────────────────────────────────────────────────────
const LevelBadge = ({ niveau }) => {
  const cfg = LEVEL_CFG[niveau] || LEVEL_CFG["Novice"];
  return (
    <span className="badge" style={{ color: cfg.color, background: cfg.bg }}>
      {cfg.icon} {niveau || "Novice"}
    </span>
  );
};

const KycBadge = ({ verified }) => (
  <span className="badge" style={{
    color:      verified ? T.green  : T.yellow,
    background: verified ? `${T.green}12` : `${T.yellow}12`,
  }}>
    {verified
      ? <><CheckCircle size={9} style={{ marginRight: 3 }} />Vérifié</>
      : <><Clock size={9} style={{ marginRight: 3 }} />En attente</>}
  </span>
);

// ─── Hacker Detail Modal (Floating Centered) ──────────────────────────────────
const HackerDetailModal = ({ hacker, open, onClose, onValidateKYC, loading }) => {
  const [tab, setTab] = useState("profil");
  const [docStatuses, setDocStatuses] = useState({ cni: null, casier: null, biometric: null });
  const [lastHackerId, setLastHackerId] = useState(null);

  if (hacker && hacker.userId !== lastHackerId) {
    setLastHackerId(hacker.userId);
    setDocStatuses({ cni: null, casier: null, biometric: null });
  }

  // Bloquer le scroll du body quand la modale est ouverte et scroller vers le haut
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!hacker && !open) return null;

  const pseudo   = hacker?.pseudo   || "—";
  const nom      = hacker?.nom      || "—";
  const prenom   = hacker?.prenom   || "—";
  const email    = hacker?.email    || "—";
  const points   = hacker?.points   ?? 0;
  const niveau   = hacker?.niveau   || "Novice";
  const avatar   = hacker?.avatar   || null;
  const bio      = hacker?.bio      || null;
  const skills   = hacker?.skills   || [];

  const kycVerified      = hacker?.kycVerified    ?? false;
  const totalReports     = hacker?.totalReports   ?? null;
  const validatedReports = hacker?.validatedReports ?? null;
  const successRate      = hacker?.successRate    ?? null;
  const programsJoined   = hacker?.programsJoined ?? null;

  const rawCni       = hacker?.rawCni       ?? null;
  const rawCasier    = hacker?.rawCasier    ?? null;
  const rawBiometric = hacker?.rawBiometric ?? null;

  const lvl         = LEVEL_RANGES[niveau] || LEVEL_RANGES["Novice"];
  const progressVal = Math.max(0, points - lvl.min);

  const submittedKeys = [
    rawCni       ? "cni"       : null,
    rawCasier    ? "casier"    : null,
    rawBiometric ? "biometric" : null,
  ].filter(Boolean);

  const allDocsApproved = submittedKeys.length > 0 &&
    submittedKeys.every(k => docStatuses[k] === "approved");
  const anyDocRejected  = submittedKeys.some(k => docStatuses[k] === "rejected");

  const setDoc = (key, status) => setDocStatuses(prev => ({ ...prev, [key]: status }));

  const modalContent = (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 1000,
          background: "rgba(0,0,0,0.45)",
          backdropFilter: "blur(4px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity .25s ease",
        }}
      />

      {/* Modal centré flottant */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: open
            ? "translate(-50%, -50%) scale(1)"
            : "translate(-50%, -50%) scale(0.96)",
          zIndex: 1001,
          width: "min(600px, 94vw)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: 18,
          border: `1px solid ${T.border}`,
          background: T.surface || T.bg,
          boxShadow: "0 32px 80px rgba(0,0,0,.35), 0 0 0 1px rgba(255,255,255,.04)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity .25s ease, transform .28s cubic-bezier(.4,0,.2,1)",
          overflow: "hidden",
        }}
      >
        {/* Header fixe */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px",
          borderBottom: `1px solid ${T.border}`,
          flexShrink: 0,
        }}>
          <div style={{ fontFamily: T.display, fontWeight: 800, fontSize: 16 }}>
            Profil Hacker
          </div>
          <button
            onClick={onClose}
            style={{
              background: `${T.muted}15`, border: "none", cursor: "pointer",
              color: T.muted, display: "flex", alignItems: "center", justifyContent: "center",
              width: 32, height: 32, borderRadius: 8, transition: "background .15s",
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Contenu scrollable */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>

          {/* Avatar + infos */}
          <div style={{
            display: "flex", alignItems: "center", gap: 18, marginBottom: 20,
            padding: "18px 20px", background: T.bg, borderRadius: 14,
            border: `1px solid ${T.border}`,
          }}>
            {avatar ? (
              <img src={avatar} alt="avatar" style={{
                width: 64, height: 64, borderRadius: "50%", objectFit: "cover",
                border: `3px solid ${T.blue}`, flexShrink: 0,
              }} />
            ) : (
              <div style={{
                width: 64, height: 64, borderRadius: "50%", flexShrink: 0,
                background: `linear-gradient(135deg, ${T.blue}, ${T.purple})`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 24, fontWeight: 900, color: "#fff",
                boxShadow: `0 0 20px ${T.blueGlow}`,
              }}>
                {(pseudo !== "—" ? pseudo : email)[0].toUpperCase()}
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: T.display, fontWeight: 800, fontSize: 20, marginBottom: 4 }}>
                {pseudo}
              </div>
              <div style={{ fontSize: 12, color: T.muted, marginBottom: 10 }}>{email}</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <LevelBadge niveau={niveau} />
                <KycBadge verified={kycVerified} />
                <span className="badge" style={{ color: T.yellow, background: `${T.yellow}12` }}>
                  <Star size={9} style={{ marginRight: 3 }} />
                  {points.toLocaleString()} pts
                </span>
              </div>
            </div>
          </div>

          {/* Barre de progression */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.muted, marginBottom: 6 }}>
              <span>Progression — {niveau}</span>
              <span>{points} / {lvl.max} pts</span>
            </div>
            <ProgressBar value={progressVal} max={Math.max(1, lvl.max - lvl.min)} color={T.blue} height={6} />
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 4, background: T.bg, borderRadius: 10, padding: 4, marginBottom: 18 }}>
            {[
              { id: "profil",  label: "Profil",       icon: UserCheck  },
              { id: "kyc",     label: "KYC & Docs",   icon: FileText   },
              { id: "stats",   label: "Statistiques", icon: TrendingUp },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                flex: 1, border: "none", borderRadius: 7, padding: "8px 12px",
                fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all .15s",
                background: tab === t.id ? T.blue : "transparent",
                color: tab === t.id ? "#fff" : T.muted,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}>
                <t.icon size={13} />{t.label}
              </button>
            ))}
          </div>

          {/* Tab : Profil */}
          {tab === "profil" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                {[
                  { l: "Prénom",     v: prenom !== "—" ? prenom : null },
                  { l: "Nom",        v: nom    !== "—" ? nom    : null },
                  { l: "Pseudo",     v: pseudo !== "—" ? pseudo : null },
                  { l: "Email",      v: email  !== "—" ? email  : null },
                  { l: "Points",     v: points.toLocaleString()         },
                  { l: "Niveau",     v: niveau                          },
                  { l: "Inscrit le", v: formatDate(hacker?.createdAt)   },
                  { l: "ID",         v: hacker?.id ? hacker.id.slice(0, 16) + "…" : null },
                ].filter(f => f.v).map((f, i) => (
                  <div key={i} style={{ background: T.bg, borderRadius: 8, padding: "10px 14px" }}>
                    <div style={{ fontSize: 9, color: T.muted, fontWeight: 700, letterSpacing: ".08em", marginBottom: 3 }}>
                      {f.l.toUpperCase()}
                    </div>
                    <div style={{ fontSize: 12, color: T.text, wordBreak: "break-all" }}>{f.v}</div>
                  </div>
                ))}
              </div>

              {bio && (
                <div style={{ background: T.bg, borderRadius: 10, padding: 14, marginBottom: 14 }}>
                  <div style={{ fontSize: 9, color: T.muted, fontWeight: 700, letterSpacing: ".08em", marginBottom: 6 }}>BIO</div>
                  <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.7 }}>{bio}</div>
                </div>
              )}

              {skills.length > 0 && (
                <div>
                  <div style={{ fontSize: 9, color: T.muted, fontWeight: 700, letterSpacing: ".08em", marginBottom: 8 }}>COMPÉTENCES</div>
                  <div>{skills.map((s, i) => <span key={i} className="tag">{s}</span>)}</div>
                </div>
              )}

              {bio === null && skills.length === 0 && (
                <div style={{
                  fontSize: 11, color: T.muted, padding: "10px 14px",
                  background: T.bg, borderRadius: 8, fontStyle: "italic",
                }}>
                  Bio et compétences non disponibles via{" "}
                  <code style={{ color: T.cyan }}>GET /admin/hackers</code>
                </div>
              )}
            </>
          )}

          {/* Tab : KYC */}
          {tab === "kyc" && (
            <>
              <div style={{
                display: "flex", alignItems: "center", gap: 12, padding: "14px 18px",
                background: kycVerified ? `${T.green}0f` : `${T.yellow}0f`,
                border: `1px solid ${kycVerified ? T.green : T.yellow}30`,
                borderRadius: 10, marginBottom: 18,
              }}>
                {kycVerified
                  ? <CheckCircle size={22} color={T.green} />
                  : <Clock size={22} color={T.yellow} />}
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: kycVerified ? T.green : T.yellow }}>
                    {kycVerified ? "KYC Vérifié ✓" : "KYC En attente de validation"}
                  </div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                    {kycVerified
                      ? "Ce hacker est vérifié et accepté dans les programmes."
                      : "Inspectez chaque document puis validez ou rejetez individuellement."}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 }}>
                <DocRow
                  icon={UserCheck}
                  label="Pièce d'identité (CNI)"
                  desc="Recto + Verso"
                  rawPath={rawCni}
                  docStatus={docStatuses.cni}
                  onApprove={() => setDoc("cni", "approved")}
                  onReject={() => setDoc("cni", "rejected")}
                  kycAlreadyVerified={kycVerified}
                />
                <DocRow
                  icon={FileText}
                  label="Casier judiciaire"
                  desc="PDF ou image"
                  rawPath={rawCasier}
                  docStatus={docStatuses.casier}
                  onApprove={() => setDoc("casier", "approved")}
                  onReject={() => setDoc("casier", "rejected")}
                  kycAlreadyVerified={kycVerified}
                />
                <DocRow
                  icon={Camera}
                  label="Vérification biométrique"
                  desc="Selfie"
                  rawPath={rawBiometric}
                  docStatus={docStatuses.biometric}
                  onApprove={() => setDoc("biometric", "approved")}
                  onReject={() => setDoc("biometric", "rejected")}
                  kycAlreadyVerified={kycVerified}
                />
              </div>

              {!kycVerified && (
                <>
                  {submittedKeys.length > 0 && (
                    <div style={{
                      padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontSize: 12,
                      background: anyDocRejected  ? "#ef444410"
                                : allDocsApproved ? `${T.green}10`
                                : `${T.yellow}10`,
                      border: `1px solid ${
                        anyDocRejected  ? "#ef444430"
                        : allDocsApproved ? `${T.green}30`
                        : `${T.yellow}30`}`,
                      color: anyDocRejected  ? "#ef4444"
                           : allDocsApproved ? T.green
                           : T.yellow,
                    }}>
                      {anyDocRejected
                        ? "⚠ Un ou plusieurs documents ont été rejetés. Le KYC ne peut pas être validé."
                        : allDocsApproved
                        ? "✓ Tous les documents sont validés. Vous pouvez confirmer le KYC."
                        : "Inspectez et statuez sur chaque document avant de confirmer le KYC."}
                    </div>
                  )}

                  <AlertBox type="warning">
                    La validation KYC est irréversible. Assurez-vous d'avoir vérifié tous les documents.
                  </AlertBox>

                  <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                    <button
                      className="btn btn-success"
                      style={{
                        flex: 1, justifyContent: "center",
                        opacity: anyDocRejected ? 0.45 : 1,
                        cursor: anyDocRejected ? "not-allowed" : "pointer",
                      }}
                      onClick={() => !anyDocRejected && onValidateKYC(hacker.userId)}
                      disabled={loading || anyDocRejected}
                    >
                      {loading
                        ? <><RefreshCw size={13} className="spin" /> Validation...</>
                        : <><Check size={14} /> Valider le KYC</>}
                    </button>
                    <button
                      className="btn btn-ghost"
                      style={{ flex: 1, justifyContent: "center" }}
                      onClick={onClose}
                    >
                      Plus tard
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* Tab : Stats */}
          {tab === "stats" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { l: "Points totaux",       v: points.toLocaleString(),                              c: T.yellow, icon: Star         },
                { l: "Niveau",              v: niveau,                                               c: T.blue,   icon: Award        },
                { l: "Rapports soumis",     v: totalReports     !== null ? totalReports     : "—",   c: T.orange, icon: FileText     },
                { l: "Rapports validés",    v: validatedReports !== null ? validatedReports : "—",   c: T.green,  icon: CheckCircle  },
                { l: "Taux de succès",      v: successRate      !== null ? `${successRate}%` : "—",  c: T.cyan,   icon: TrendingUp   },
                { l: "Programmes rejoints", v: programsJoined   !== null ? programsJoined   : "—",   c: T.purple, icon: ChevronRight },
              ].map((s, i) => (
                <div key={i} style={{
                  background: T.bg, borderRadius: 10, padding: "14px 16px",
                  border: `1px solid ${T.border}`,
                  display: "flex", alignItems: "center", gap: 12,
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                    background: `${s.c}15`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <s.icon size={14} color={s.c} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, color: T.muted, fontWeight: 700, letterSpacing: ".06em", marginBottom: 3 }}>
                      {s.l.toUpperCase()}
                    </div>
                    <div style={{ fontFamily: T.display, fontWeight: 800, fontSize: 16, color: s.c }}>{s.v}</div>
                  </div>
                </div>
              ))}

              {totalReports === null && (
                <div style={{
                  gridColumn: "1 / -1", fontSize: 11, color: T.muted,
                  fontStyle: "italic", padding: "10px 14px",
                  background: T.bg, borderRadius: 8,
                }}>
                  Statistiques détaillées disponibles via{" "}
                  <code style={{ color: T.cyan }}>GET /admin/classement-hackers</code>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};


// ─── Main Page ────────────────────────────────────────────────────────────────
const HackersPage = ({ token }) => {
  const { data: raw, loading, refetch } = useFetch(ENDPOINTS.adminHackers, token);
  const { toasts, show, remove }        = useToast();
  const modal                           = useModal();
  const [search, setSearch]             = useState("");
  const [kycFilter, setKycFilter]       = useState("all");
  const [levelFilter, setLevelFilter]   = useState("all");
  const [kycLoading, setKycLoading]     = useState(false);
  const [kycOverrides, setKycOverrides] = useState({});

  const hackers = useMemo(() => {
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : [];

    if (list.length === 0) return [];

    const seen   = new Set();
    const result = [];

    for (const entry of list) {
      const h   = entry.hacker ?? entry;
      const uid = h.user?.id ?? h.id;

      if (uid && seen.has(uid)) continue;
      if (uid) seen.add(uid);

      result.push({
        id:      h.id,
        pseudo:  h.pseudo   ?? "—",
        nom:     h.nom      ?? "—",
        prenom:  h.prenom   ?? "—",
        avatar:  h.avatar   ?? null,
        niveau:  h.niveau   ?? "Novice",
        points:  h.points   ?? 0,
        bio:     h.aPropos  ?? null,
        skills:  h.skills   ?? [],
        createdAt: h.user?.createdAt ?? h.createdAt,

        userId:      h.user?.id               ?? h.id,
        email:       h.user?.email            ?? "—",
        kycVerified: h.user?.kycVerifie       ?? false,
        docSet:      h.user?.docSet           ?? false,

        rawCni:             h.user?.pieceIdentite    ?? null,
        rawCasier:          h.user?.casierJudiciaire ?? null,
        rawBiometric:       h.user?.selfieKyc        ?? null,
        cniSubmitted:       !!h.user?.pieceIdentite,
        casierSubmitted:    !!h.user?.casierJudiciaire,
        biometricSubmitted: !!h.user?.selfieKyc,

        totalReports:     entry.totalReports     ?? null,
        validatedReports: entry.validatedReports ?? null,
        successRate:      entry.successRate      ?? null,
        programsJoined:   entry.programsJoined   ?? null,
      });
    }

    return result;
  }, [raw]);

  const hackersWithOverrides = useMemo(() =>
    hackers.map(h => ({
      ...h,
      kycVerified: kycOverrides[h.userId] ?? h.kycVerified,
    })),
  [hackers, kycOverrides]);

  const isKycVerified = (h) => h.kycVerified ?? false;

  const validateKYC = async (userId) => {
    if (!userId) {
      show("❌ Identifiant utilisateur introuvable", "error");
      return;
    }
    setKycLoading(true);
    try {
      const res = await api(
        ENDPOINTS.validateKYC(userId),
        { method: "PUT", body: JSON.stringify({ docSet: true }) },
        token
      );

      if (res?.success === false) {
        throw new Error(res.message || "Échec de la validation");
      }

      setKycOverrides(prev => ({ ...prev, [userId]: true }));
      show("✅ KYC validé avec succès !");
      modal.close();
      refetch();
    } catch (e) {
      show(e.message || "Erreur lors de la validation KYC", "error");
    } finally {
      setKycLoading(false);
    }
  };

  const filtered = hackersWithOverrides.filter(h => {
    if (search &&
      !h.pseudo?.toLowerCase().includes(search.toLowerCase()) &&
      !h.email?.toLowerCase().includes(search.toLowerCase()) &&
      !h.nom?.toLowerCase().includes(search.toLowerCase())
    ) return false;
    if (kycFilter === "verified" && !isKycVerified(h)) return false;
    if (kycFilter === "pending"  &&  isKycVerified(h)) return false;
    if (levelFilter !== "all"   && h.niveau !== levelFilter) return false;
    return true;
  });

  const total          = hackersWithOverrides.length;
  const pendingKyc     = hackersWithOverrides.filter(h => !isKycVerified(h)).length;
  const experts        = hackersWithOverrides.filter(h => h.niveau === "Expert").length;
  const intermediaires = hackersWithOverrides.filter(h =>
    h.niveau === "Intermédiaire" || h.niveau === "Intermediaire"
  ).length;

  const modalHacker = useMemo(() =>
    modal.data
      ? hackersWithOverrides.find(h => h.userId === modal.data.userId) ?? modal.data
      : null,
  [modal.data, hackersWithOverrides]);

  return (
    <div className="fade-up" style={{ padding: "24px 28px", overflowY: "auto", flex: 1 }}>
      <SectionHeader
        title="Gestion des Hackers"
        sub={`${total} hacker(s) inscrit(s) · ${pendingKyc} KYC en attente`}
        action={
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Rechercher pseudo, email..."
            style={{ width: 260 }}
          />
        }
      />

      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 22 }}>
        <Card style={{ padding: "18px 20px" }}>
          <div style={{ fontSize: 11, color: T.muted, marginBottom: 10 }}>Total Hackers</div>
          <div style={{ fontFamily: T.display, fontWeight: 900, fontSize: 30, color: T.blue }}>{total}</div>
        </Card>
        <Card style={{ padding: "18px 20px" }}>
          <div style={{ fontSize: 11, color: T.muted, marginBottom: 10 }}>KYC à valider</div>
          <div style={{ fontFamily: T.display, fontWeight: 900, fontSize: 30, color: pendingKyc > 0 ? T.yellow : T.green }}>
            {pendingKyc}
          </div>
          {pendingKyc > 0 && (
            <div style={{ fontSize: 10, color: T.yellow, marginTop: 4 }}>⚠ Action requise</div>
          )}
        </Card>
        <Card style={{ padding: "18px 20px" }}>
          <div style={{ fontSize: 11, color: T.muted, marginBottom: 10 }}>⚡ Experts</div>
          <div style={{ fontFamily: T.display, fontWeight: 900, fontSize: 30, color: T.blue }}>{experts}</div>
        </Card>
        <Card style={{ padding: "18px 20px" }}>
          <div style={{ fontSize: 11, color: T.muted, marginBottom: 10 }}>🔥 Intermédiaires</div>
          <div style={{ fontFamily: T.display, fontWeight: 900, fontSize: 30, color: T.purple }}>{intermediaires}</div>
        </Card>
      </div>

      {/* Filtres */}
      <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <Tabs
          tabs={[
            { id: "all",      label: "Tous",           count: total },
            { id: "verified", label: "KYC Vérifiés",   count: total - pendingKyc },
            { id: "pending",  label: "KYC En attente", count: pendingKyc },
          ]}
          active={kycFilter}
          onChange={setKycFilter}
        />
        <Tabs
          tabs={[
            { id: "all",           label: "Tous niveaux" },
            { id: "Expert",        label: "⚡ Expert" },
            { id: "Intermédiaire", label: "🔥 Intermédiaire" },
            { id: "Novice",        label: "🌱 Novice" },
          ]}
          active={levelFilter}
          onChange={setLevelFilter}
        />
      </div>

      {/* Table */}
      {loading ? <Spinner /> : filtered.length === 0 ? (
        <EmptyState icon={Users} title="Aucun hacker trouvé" sub="Modifiez les filtres de recherche" />
      ) : (
        <Card>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>PSEUDO</th>
                <th>EMAIL</th>
                <th>NIVEAU</th>
                <th>POINTS</th>
                <th>KYC</th>
                <th>INSCRIT LE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((h, i) => (
                <tr
                  key={h.id || i}
                  className="hov-row"
                  style={{ cursor: "pointer" }}
                  onClick={() => modal.openWith(h)}
                >
                  <td style={{ color: T.muted, fontWeight: 700 }}>{i + 1}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {h.avatar ? (
                        <img src={h.avatar} alt="" style={{
                          width: 30, height: 30, borderRadius: "50%",
                          objectFit: "cover", border: `2px solid ${T.border}`, flexShrink: 0,
                        }} />
                      ) : (
                        <div style={{
                          width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                          background: `hsl(${(h.pseudo || "?").charCodeAt(0) * 13 % 360}, 50%, 35%)`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 800, color: "#fff",
                        }}>
                          {(h.pseudo || "?")[0].toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{h.pseudo || "—"}</div>
                        {h.bio && (
                          <div style={{
                            fontSize: 10, color: T.muted, maxWidth: 160,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>{h.bio}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={{ color: T.textSub, fontSize: 12 }}>{h.email}</td>
                  <td><LevelBadge niveau={h.niveau} /></td>
                  <td>
                    <span style={{ fontFamily: T.display, fontWeight: 800, fontSize: 14, color: T.yellow }}>
                      {(h.points || 0).toLocaleString()}
                    </span>
                  </td>
                  <td><KycBadge verified={isKycVerified(h)} /></td>
                  <td style={{ color: T.muted, fontSize: 11 }}>{formatDate(h.createdAt)}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        className="btn btn-ghost btn-sm btn-icon"
                        onClick={() => modal.openWith(h)}
                        title="Voir profil"
                      >
                        <Eye size={13} />
                      </button>
                      {!isKycVerified(h) && (
                        <button
                          className="btn btn-success btn-sm"
                          onClick={(e) => { e.stopPropagation(); validateKYC(h.userId); }}
                          disabled={kycLoading}
                          title="Valider KYC directement"
                        >
                          {kycLoading
                            ? <RefreshCw size={12} className="spin" />
                            : <><Check size={12} /> KYC</>}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{
            padding: "12px 20px", borderTop: `1px solid ${T.border}`,
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <span style={{ fontSize: 12, color: T.muted }}>
              {filtered.length} résultat(s) sur {total}
            </span>
          </div>
        </Card>
      )}

      <HackerDetailModal
        hacker={modalHacker}
        open={modal.open}
        onClose={modal.close}
        onValidateKYC={validateKYC}
        loading={kycLoading}
      />

      <ToastStack toasts={toasts} remove={remove} />
    </div>
  );
};

export default HackersPage;